/**
 * PerspectiveWarp.js
 *
 * Applies quadrilateral perspective transforms to images for the classic
 * "magazine on a table" look.  The warp maps a rectangular source region
 * onto an arbitrary quadrilateral defined by four destination corner points,
 * using a scanline-based bilinear interpolation approach rendered entirely
 * through the Canvas 2D API.
 *
 * For performance the image is sliced into horizontal scanlines, each drawn
 * as a thin trapezoidal strip via ctx.setTransform().  This avoids per-pixel
 * manipulation while still producing a convincing perspective effect.
 *
 * @module PerspectiveWarp
 */

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Linearly interpolate between two {x,y} points.
 * @param {{x:number,y:number}} a
 * @param {{x:number,y:number}} b
 * @param {number} t  – 0-1
 * @returns {{x:number,y:number}}
 */
function lerp2D(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/**
 * Distance between two points.
 * @param {{x:number,y:number}} a
 * @param {{x:number,y:number}} b
 * @returns {number}
 */
function dist(a, b) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/* ------------------------------------------------------------------ */
/*  Core renderer                                                      */
/* ------------------------------------------------------------------ */

/**
 * Render a source image region warped onto an arbitrary quadrilateral.
 *
 * The approach divides the quadrilateral into horizontal scanline strips
 * (from top edge to bottom edge).  For each strip a 2-D affine transform
 * is computed that maps the corresponding source row to the destination
 * trapezoid sliver.
 *
 * @param {CanvasRenderingContext2D} ctx – destination context
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} image – source
 * @param {{ x:number, y:number, w:number, h:number }} srcRect
 *        Source rectangle in the image to sample from.  Use
 *        `{ x:0, y:0, w:image.width, h:image.height }` for the full image.
 * @param {{ topLeft:{x:number,y:number}, topRight:{x:number,y:number},
 *           bottomLeft:{x:number,y:number}, bottomRight:{x:number,y:number} }} destQuad
 *        Four corners of the destination quadrilateral in canvas coordinates.
 * @param {object} [options]
 * @param {number} [options.subdivisions] – number of horizontal strips
 *        (defaults to an adaptive value based on quad height)
 */
export function renderPerspective(ctx, image, srcRect, destQuad, options = {}) {
  if (!image) return;

  const { topLeft, topRight, bottomLeft, bottomRight } = destQuad;

  // Adaptive subdivision count — more strips for taller quads
  const quadHeight = Math.max(
    dist(topLeft, bottomLeft),
    dist(topRight, bottomRight)
  );
  const subdivisions = options.subdivisions || Math.max(40, Math.ceil(quadHeight / 3));

  const srcX = srcRect.x;
  const srcY = srcRect.y;
  const srcW = srcRect.w;
  const srcH = srcRect.h;

  ctx.save();

  for (let i = 0; i < subdivisions; i++) {
    const t0 = i / subdivisions;
    const t1 = (i + 1) / subdivisions;

    // Destination corners for this strip
    const dTL = lerp2D(topLeft, bottomLeft, t0);
    const dTR = lerp2D(topRight, bottomRight, t0);
    const dBL = lerp2D(topLeft, bottomLeft, t1);
    const dBR = lerp2D(topRight, bottomRight, t1);

    // Source row for this strip
    const sy = srcY + srcH * t0;
    const sh = (srcH / subdivisions) + 0.5; // +0.5 to prevent gaps

    // We draw the strip by computing the affine transform that maps:
    //   (0, 0)     → dTL
    //   (srcW, 0)  → dTR
    //   (0, sh)    → dBL
    //
    // setTransform(a, b, c, d, e, f) maps (x,y) → (ax+cy+e, bx+dy+f)
    const a = (dTR.x - dTL.x) / srcW;
    const b = (dTR.y - dTL.y) / srcW;
    const c = (dBL.x - dTL.x) / sh;
    const d = (dBL.y - dTL.y) / sh;
    const e = dTL.x;
    const f = dTL.y;

    ctx.save();
    ctx.setTransform(a, b, c, d, e, f);

    // Clip to the strip quadrilateral to prevent bleed from the affine
    // approximation (since a true perspective quad is not perfectly affine).
    // We clip in transformed coordinates.
    const brLocal = {
      x: srcW,
      y: sh,
    };
    // In local coords the quad corners are (0,0), (srcW,0), brLocal, (0,sh)
    // but the bottom-right corner of the destination strip (dBR) may not
    // exactly match the affine mapping of (srcW, sh).  For thin strips the
    // error is negligible, so we just draw and clip a generous rect.
    ctx.beginPath();
    ctx.moveTo(-1, -0.5);
    ctx.lineTo(srcW + 1, -0.5);
    ctx.lineTo(srcW + 1, sh + 0.5);
    ctx.lineTo(-1, sh + 0.5);
    ctx.closePath();
    ctx.clip();

    ctx.drawImage(image, srcX, sy, srcW, sh, 0, 0, srcW, sh);

    ctx.restore();
  }

  ctx.restore();
}

/* ------------------------------------------------------------------ */
/*  Quad generation helper                                             */
/* ------------------------------------------------------------------ */

/**
 * Generate a destination quadrilateral from intuitive parameters.
 *
 * The quad starts as a flat rectangle at (x, y, width, height) and is then
 * modified by the supplied rotation / skew / tilt parameters to simulate
 * perspective foreshortening.
 *
 * @param {number} x      – left
 * @param {number} y      – top
 * @param {number} width
 * @param {number} height
 * @param {object} [options]
 * @param {number} [options.rotateY=0]    – degrees; positive = right side
 *        recedes (simulates Y-axis rotation)
 * @param {number} [options.tiltX=0]      – degrees; positive = top recedes
 *        (simulates X-axis tilt — "on a table" look)
 * @param {number} [options.skewX=0]      – horizontal skew in pixels
 * @param {number} [options.skewY=0]      – vertical skew in pixels
 * @param {number} [options.vanishRatio]  – 0-1, how much the far side
 *        shrinks (auto-calculated from rotateY if not supplied)
 * @returns {{ topLeft:{x:number,y:number}, topRight:{x:number,y:number},
 *             bottomLeft:{x:number,y:number}, bottomRight:{x:number,y:number} }}
 */
export function createPerspectiveQuad(x, y, width, height, options = {}) {
  const {
    rotateY = 0,
    tiltX = 0,
    skewX = 0,
    skewY = 0,
    vanishRatio,
  } = options;

  // Start with a flat rectangle
  let tl = { x, y };
  let tr = { x: x + width, y };
  let bl = { x, y: y + height };
  let br = { x: x + width, y: y + height };

  // ----- Y-axis rotation (left/right perspective) -----
  if (rotateY !== 0) {
    // Convert to a 0-1 foreshortening factor
    const angle = (Math.abs(rotateY) / 90); // 0-1 normalised
    const shrink = vanishRatio ?? (1 - angle * 0.55); // far side height shrinks
    const shift = angle * width * 0.10; // slight horizontal inset

    if (rotateY > 0) {
      // Right side recedes
      const midY = y + height / 2;
      const newH = height * shrink;
      tr = { x: tr.x - shift, y: midY - newH / 2 };
      br = { x: br.x - shift, y: midY + newH / 2 };
    } else {
      // Left side recedes
      const midY = y + height / 2;
      const newH = height * shrink;
      tl = { x: tl.x + shift, y: midY - newH / 2 };
      bl = { x: bl.x + shift, y: midY + newH / 2 };
    }
  }

  // ----- X-axis tilt (top recedes — "on a table") -----
  if (tiltX !== 0) {
    const angle = (Math.abs(tiltX) / 90);
    const shrinkW = 1 - angle * 0.45; // top edge gets narrower
    const shift = angle * height * 0.08; // slight vertical inset
    const cx = (tl.x + tr.x) / 2;

    if (tiltX > 0) {
      // Top recedes
      const topW = dist(tl, tr) * shrinkW;
      tl = { x: cx - topW / 2, y: tl.y + shift };
      tr = { x: cx + topW / 2, y: tr.y + shift };
    } else {
      // Bottom recedes
      const botW = dist(bl, br) * shrinkW;
      const bCx = (bl.x + br.x) / 2;
      bl = { x: bCx - botW / 2, y: bl.y - shift };
      br = { x: bCx + botW / 2, y: br.y - shift };
    }
  }

  // ----- Skew -----
  if (skewX !== 0) {
    tl.x += skewX;
    tr.x += skewX;
  }
  if (skewY !== 0) {
    tr.y += skewY;
    br.y += skewY;
  }

  return { topLeft: tl, topRight: tr, bottomLeft: bl, bottomRight: br };
}

/**
 * Render a perspective-warped image onto a new canvas and return it.
 *
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} image
 * @param {number} canvasWidth  – output canvas width
 * @param {number} canvasHeight – output canvas height
 * @param {{ topLeft:{x:number,y:number}, topRight:{x:number,y:number},
 *           bottomLeft:{x:number,y:number}, bottomRight:{x:number,y:number} }} destQuad
 * @param {object} [options] – passed through to renderPerspective
 * @returns {HTMLCanvasElement}
 */
export function renderPerspectiveToCanvas(image, canvasWidth, canvasHeight, destQuad, options = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');

  const srcRect = {
    x: 0,
    y: 0,
    w: image.width || image.naturalWidth || canvasWidth,
    h: image.height || image.naturalHeight || canvasHeight,
  };

  renderPerspective(ctx, image, srcRect, destQuad, options);
  return canvas;
}
